import { useCreateLot, useRegisterMovement } from '@/api/hooks';
import { LotAnimalPicker } from '@/components/lots/LotAnimalPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { getErrorMessage } from '@/lib/api';
import { todayIso } from '@/lib/format';
import { LOT_TYPES } from '@/lib/lotTypes';
import { useEffect, useState } from 'react';

type CreatedLot = {
  id: number;
  name: string;
};

export function CreateSaleHerdLotModal({
  open,
  onClose,
  propertyId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number | null;
  onCreated: (lot: CreatedLot) => void;
}) {
  const createLot = useCreateLot(propertyId ?? 0);
  const transfer = useRegisterMovement('transfer');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const saleType = LOT_TYPES.find((t) => t.value === 'sale')!;
  const SaleIcon = saleType.icon;

  useEffect(() => {
    if (!open) return;
    setName('');
    setCode('');
    setSelectedIds([]);
    setError(null);
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!propertyId) return;
    setError(null);

    if (selectedIds.length === 0) {
      setError('Selecione ao menos um animal sem lote.');
      return;
    }

    try {
      const res = await createLot.mutateAsync({
        name,
        code: code || null,
        lot_type: 'sale',
      });

      await transfer.mutateAsync({
        property_id: propertyId,
        movement_date: todayIso(),
        scope: 'animal',
        notes: `Composição do lote de venda ${res.lot.name}`,
        animal_ids: selectedIds,
        destination_lot_id: res.lot.id,
        is_off_property: false,
      });

      onCreated({ id: res.lot.id, name: res.lot.name });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao criar lote de venda.'));
    }
  }

  const loading = createLot.isPending || transfer.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo lote de venda"
      size="lg"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-sale-herd-lot-form" loading={loading} disabled={!propertyId}>
            Criar lote com animais
          </Button>
        </>
      }
    >
      <form id="create-sale-herd-lot-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-2xl border border-manejai-forest bg-manejai-forest/5 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-manejai-forest text-white">
              <SaleIcon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <p className="text-sm font-medium text-manejai-forest">{saleType.label}</p>
              <p className="text-xs text-manejai-bark/60">
                Monte o lote com animais que ainda não estão em outro lote.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Nome do lote"
            required
            placeholder="Ex.: Embarque julho"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="Código"
            placeholder="Opcional"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>

        {propertyId ? (
          <LotAnimalPicker
            propertyId={propertyId}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
          />
        ) : (
          <p className="text-sm text-amber-700">Selecione uma propriedade para listar animais.</p>
        )}
      </form>
    </Modal>
  );
}
